import {
  readPreviousAnswer,
  isChoiceList,
  type ChoiceValue,
  type AnswerJson,
} from "../answerJson";
import {
  decodeVisualGroups,
  type VisualGrouping,
  type RawVisualGrouping,
} from "../visualGroups";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";
import { encodeResponses } from "../responseEncoding";
import { restoreChoice } from "../specify/core";

export type MultiSelectRaw = RawVisualGrouping & {
  type: "multi-select";
  content: string;
  style: string;
  max_options: number;
  options: {
    value: number;
    text: string;
    disabled: boolean;
    unique: boolean;
    display_text: boolean;
    image?: string;
    pop_up_text?: string | null;
  }[];
  previous_response?: (number | string)[];
  previous_answer_json?: unknown;
  image?: string;
  alt_text?: string | null;
  error_message?: string;
};

export type MultiSelectOption = {
  id: number;
  label: string;
  disabled: boolean;
  exclusive: boolean;
  displayText: boolean;
  image?: string;
  customPrompt?: string;
};

export type MultiSelectDefinition = VisualGrouping & {
  prompt: string;
  options: readonly MultiSelectOption[];
  maxSelections: number;
  previousResponses: readonly ChoiceValue[];
  image?: string;
  altText?: string;
  error?: string;
};

export type MultiSelectDraft = {
  selectedIds: readonly number[];
  customTexts: Readonly<Record<number, string>>;
};

export type MultiSelectEvent =
  | { type: "toggle"; id: number }
  | { type: "replace-selection"; ids: readonly number[] }
  | { type: "set-custom-answer"; id: number; value: string };

export type MultiSelectResponse = {
  type: "multi-select";
  value: string;
  responses: string;
  answerJson: AnswerJson;
};

const ordered = (definition: MultiSelectDefinition, ids: readonly number[]) =>
  definition.options
    .filter((option) => ids.includes(option.id))
    .map((option) => option.id);

export const canToggleMultiSelectOption = (
  definition: MultiSelectDefinition,
  draft: MultiSelectDraft,
  id: number,
): boolean => {
  const option = definition.options.find((item) => item.id === id);
  if (!option || option.disabled) return false;
  if (draft.selectedIds.includes(id) || option.exclusive) return true;
  const selectedOrdinaryCount = draft.selectedIds.filter(
    (selectedId) =>
      !definition.options.find((item) => item.id === selectedId)?.exclusive,
  ).length;
  return selectedOrdinaryCount < definition.maxSelections;
};

const restore = (definition: MultiSelectDefinition): MultiSelectDraft => {
  const ids: number[] = [];
  const customTexts: Record<number, string> = {};
  for (const raw of definition.previousResponses) {
    const restored = restoreChoice(definition.options, raw);
    const option = definition.options.find(
      (item) => item.id === restored?.optionId,
    );
    if (!restored || !option || ids.includes(option.id)) continue;
    if (restored.customText !== undefined)
      customTexts[option.id] = restored.customText;
    if (option.customPrompt !== undefined && !customTexts[option.id]) continue;
    if (option.exclusive) {
      ids.splice(0, ids.length, option.id);
      break;
    }
    if (ids.length < definition.maxSelections) ids.push(option.id);
  }
  return {
    selectedIds: ordered(definition, ids),
    customTexts,
  };
};

export const multiSelectCore: QuestionCore<
  MultiSelectRaw,
  MultiSelectDefinition,
  MultiSelectDraft,
  MultiSelectEvent,
  MultiSelectResponse
> = {
  decode: (raw) => {
    if (!Array.isArray(raw.options) || raw.options.length === 0) {
      throw new Error("Multi-select requires options.");
    }
    if (!Number.isInteger(raw.max_options) || raw.max_options < 1) {
      throw new Error("Multi-select has an invalid maximum.");
    }
    if (
      raw.previous_answer_json === undefined &&
      raw.previous_response !== undefined &&
      !Array.isArray(raw.previous_response)
    ) {
      throw new Error("Multi-select previous response must be a list.");
    }
    if (
      raw.previous_answer_json === undefined &&
      raw.previous_response?.some(
        (value) => typeof value !== "string" && typeof value !== "number",
      )
    ) {
      throw new Error(
        "Multi-select previous responses must be strings or numbers.",
      );
    }
    const ids = raw.options.map((option) => option.value);
    if (
      ids.some((id) => !Number.isInteger(id)) ||
      new Set(ids).size !== ids.length
    ) {
      throw new Error("Multi-select option IDs must be unique integers.");
    }
    return {
      ...decodeVisualGroups(raw),
      prompt: raw.content,
      options: raw.options.map((option) => ({
        id: option.value,
        label: option.text,
        disabled: option.disabled,
        exclusive: option.unique,
        displayText: option.display_text,
        ...(option.image ? { image: option.image } : {}),
        ...(option.pop_up_text ? { customPrompt: option.pop_up_text } : {}),
      })),
      maxSelections: raw.max_options,
      previousResponses: readPreviousAnswer<ChoiceValue[]>(
        raw.previous_answer_json,
        raw.previous_response ?? [],
        isChoiceList,
      ),
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
    };
  },
  initialize: restore,
  update: (definition, draft, event) => {
    if (event.type === "replace-selection") {
      let next: MultiSelectDraft = {
        ...draft,
        selectedIds: draft.selectedIds.filter((id) => event.ids.includes(id)),
      };
      for (const id of event.ids) {
        if (!next.selectedIds.includes(id)) {
          next = multiSelectCore.update(definition, next, {
            type: "toggle",
            id,
          });
        }
      }
      return next;
    }
    const option = definition.options.find((item) => item.id === event.id);
    if (!option || option.disabled) return draft;
    if (event.type === "set-custom-answer") {
      if (
        option.customPrompt === undefined ||
        !event.value.trim() ||
        !canToggleMultiSelectOption(definition, draft, option.id)
      )
        return draft;
      const remaining = draft.selectedIds.filter(
        (id) =>
          id !== option.id &&
          !definition.options.find((item) => item.id === id)?.exclusive,
      );
      return {
        selectedIds: ordered(
          definition,
          option.exclusive ? [option.id] : [...remaining, option.id],
        ),
        customTexts: { ...draft.customTexts, [option.id]: event.value },
      };
    }
    if (draft.selectedIds.includes(option.id)) {
      const customTexts = { ...draft.customTexts };
      delete customTexts[option.id];
      return {
        ...draft,
        selectedIds: draft.selectedIds.filter((id) => id !== option.id),
        customTexts,
      };
    }
    if (option.customPrompt !== undefined) return draft;
    if (option.exclusive) return { ...draft, selectedIds: [option.id] };
    const remaining = draft.selectedIds.filter(
      (id) => !definition.options.find((item) => item.id === id)?.exclusive,
    );
    if (!canToggleMultiSelectOption(definition, draft, option.id)) return draft;
    return {
      ...draft,
      selectedIds: ordered(definition, [...remaining, option.id]),
    };
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) => {
    if (draft.selectedIds.length === 0)
      return { valid: false, error: copy.validation.selectAtLeastOne };
    if (draft.selectedIds.length > definition.maxSelections)
      return { valid: false, error: copy.validation.tooManyOptions };
    const selected = definition.options.filter((option) =>
      draft.selectedIds.includes(option.id),
    );
    if (
      selected.length !== draft.selectedIds.length ||
      selected.some((option) => option.disabled)
    ) {
      return { valid: false, error: copy.validation.invalidOption };
    }
    if (selected.length > 1 && selected.some((option) => option.exclusive)) {
      return { valid: false, error: copy.validation.exclusiveOptions };
    }
    if (
      selected.some(
        (option) =>
          option.customPrompt && !draft.customTexts[option.id]?.trim(),
      )
    ) {
      return { valid: false, error: copy.validation.multiSelectCustomAnswer };
    }
    return { valid: true };
  },
  encode: (definition, draft) => {
    const validation = multiSelectCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    const selected = definition.options.filter((option) =>
      draft.selectedIds.includes(option.id),
    );
    return {
      type: "multi-select",
      answerJson: selected.map((option) =>
        option.customPrompt !== undefined
          ? { custom: draft.customTexts[option.id] }
          : { option: option.id },
      ),
      value: selected.map((option) => option.id).join(","),
      responses: encodeResponses(
        selected.map((option) =>
          option.customPrompt ? draft.customTexts[option.id] : option.id,
        ),
      ),
    };
  },
  projectTranscript: (definition, draft) => ({
    kind: "multi-select",
    question: definition.prompt,
    answer: definition.options
      .filter((option) => draft.selectedIds.includes(option.id))
      .map((option) =>
        option.customPrompt
          ? `${option.label}: ${draft.customTexts[option.id]}`
          : option.label,
      )
      .join(", "),
  }),
};
